import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Download, Trash2, Edit2, Calendar, Clock, Search, ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_ENDPOINTS } from "../../../constants/config";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getFullName, getTeacherProfileUrl, getStaffProfileUrl } from "../../../utils/urlUtils";
import Pagination from "../../Common/Pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const LeaveManagement = () => {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all"); // "all" | "teacher" | "staff"
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editForm, setEditForm] = useState({
    status: "",
    remarks: "",
  });

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleEmployeeClick = (leave) => {
    if (leave.teacher) {
      navigate(getTeacherProfileUrl(leave.teacher));
    } else if (leave.staff) {
      navigate(getStaffProfileUrl(leave.staff));
    }
  };

  const getEmployee = (leave) => {
    return leave.teacher || leave.staff;
  };

  const getEmployeeName = (leave) => {
    const employee = getEmployee(leave);
    return getFullName(employee);
  };

  const getEmployeeCode = (leave) => {
    const employee = getEmployee(leave);
    // Teachers use employeeCode, staff use staffId
    if (leave.teacher) {
      return employee?.employeeCode || "";
    } else if (leave.staff) {
      return employee?.staffId || "";
    }
    return "";
  };

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.leave, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        console.log("Received leave data:", data.data);
        setLeaves(data.data);
      } else {
        throw new Error(data.message || "Failed to fetch leaves");
      }
    } catch (error) {
      setMessage({
        type: "error",
        content: "Error fetching leave requests: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        API_ENDPOINTS.leaveById(selectedLeave._id),
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: editForm.status,
            remarks: editForm.remarks,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchLeaves();
        setMessage({
          type: "success",
          content: "Leave request updated successfully",
        });
        setShowEditDialog(false);
        setSelectedLeave(null);
        setEditForm({ status: "", remarks: "" });
      } else {
        throw new Error(data.message || "Failed to update leave");
      }
    } catch (error) {
      setMessage({
        type: "error",
        content: "Error updating leave: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        API_ENDPOINTS.leaveById(selectedLeave._id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchLeaves();
        setMessage({
          type: "success",
          content: "Leave request deleted successfully",
        });
        setShowDeleteDialog(false);
        setSelectedLeave(null);
      } else {
        throw new Error(data.message || "Failed to delete leave");
      }
    } catch (error) {
      setMessage({
        type: "error",
        content: "Error deleting leave: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-amber-100 text-amber-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>;
      case "rejected":
        return <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>;
      default:
        return <div className="w-2 h-2 rounded-full bg-amber-500 mr-2"></div>;
    }
  };



  const sortLeaveData = (data, column, direction) => {
    return [...data].sort((a, b) => {
      let valueA, valueB;
      
      if (column === "name") {
        valueA = getEmployeeName(a).toLowerCase();
        valueB = getEmployeeName(b).toLowerCase();
      } else if (column === "employeeCode") {
        valueA = getEmployeeCode(a).toLowerCase();
        valueB = getEmployeeCode(b).toLowerCase();
      } else if (column === "date") {
        valueA = new Date(a.startDate).getTime();
        valueB = new Date(b.startDate).getTime();
      } else if (column === "leaveType") {
        valueA = (a.type || "").toLowerCase();
        valueB = (b.type || "").toLowerCase();
      } else if (column === "status") {
        valueA = (a.status || "").toLowerCase();
        valueB = (b.status || "").toLowerCase();
      }
      
      if (direction === "asc") {
        return typeof valueA === "number" ? valueA - valueB : valueA.localeCompare(valueB);
      } else {
        return typeof valueA === "number" ? valueB - valueA : valueB.localeCompare(valueA);
      }
    });
  };

  const handleLeaveSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const getSortIcon = (column, currentSortColumn, currentSortDirection) => {
    if (column !== currentSortColumn) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    }
    
    return currentSortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterRole, sortColumn, sortDirection]);

  const filteredLeaves = leaves
    .filter((leave) => {
      const employeeName = getEmployeeName(leave).toLowerCase();
      const employeeCode = getEmployeeCode(leave).toLowerCase();
      const searchLower = searchQuery.toLowerCase();
      const matchesRole =
        filterRole === "all" ||
        (filterRole === "teacher" && leave.teacher) ||
        (filterRole === "staff" && leave.staff);
      return (
        (employeeName.includes(searchLower) ||
          employeeCode.includes(searchLower)) &&
        (filterStatus === "all" || leave.status === filterStatus) &&
        matchesRole
      );
    });

  const sortedLeaves = sortLeaveData(filteredLeaves, sortColumn, sortDirection);
  const paginatedLeaves = sortedLeaves.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-full mx-auto">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <span className="text-[#0263cc] text-xl font-semibold">Leave Management</span>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                />
              </div>
              
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-40 bg-white border-[#0263cc]/20 focus:ring-[#0263cc]/30">
                  <SelectValue placeholder="Teacher / Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Teacher & Staff)</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 bg-white border-[#0263cc]/20 focus:ring-[#0263cc]/30">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                onClick={fetchLeaves}
                className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
              >
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {message.content && (
            <Alert
              variant={message.type === "error" ? "destructive" : "default"}
              className={`m-4 rounded-lg ${
                message.type === "success" ? "bg-green-50 text-green-800 border-green-200" : 
                message.type === "error" ? "bg-red-50 text-red-800 border-red-200" : 
                "bg-blue-50 text-blue-800 border-blue-200"
              }`}
            >
              <AlertDescription className="font-medium">
                {message.content}
              </AlertDescription>
            </Alert>
          )}
          
          {loading && !leaves.length ? (
            <div className="flex justify-center items-center p-8">
              <div className="flex items-center space-x-2">
                <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
                <span className="text-gray-600">Loading data...</span>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-y border-gray-200">
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLeaveSort("employeeCode")}
                    >
                      <div className="flex items-center">
                        Employee ID
                        {getSortIcon("employeeCode", sortColumn, sortDirection)}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLeaveSort("name")}
                    >
                      <div className="flex items-center">
                        Employee Name
                        {getSortIcon("name", sortColumn, sortDirection)}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLeaveSort("leaveType")}
                    >
                      <div className="flex items-center">
                        Leave Type
                        {getSortIcon("leaveType", sortColumn, sortDirection)}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLeaveSort("date")}
                    >
                      <div className="flex items-center">
                        Duration
                        {getSortIcon("date", sortColumn, sortDirection)}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLeaveSort("status")}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon("status", sortColumn, sortDirection)}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        {searchQuery ? "No leave requests found matching your search" : "No leave requests available"}
                      </td>
                    </tr>
                  ) : (
                    paginatedLeaves.map((leave) => (
                      <tr key={leave._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {getEmployeeCode(leave) || "N/A"}
                        </td>
                        <td className="px-4 py-4">
                          <span
                              className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium"
                              onClick={() => handleEmployeeClick(leave)}>
                            {getEmployeeName(leave)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Badge className="bg-[#0263cc]/10 text-[#0263cc] border border-[#0263cc]/20 hover:bg-[#0263cc]/20 capitalize">
                            {leave.leaveType ? `${leave.leaveType.name} (${leave.leaveType.code})` : (leave.type || "—")}
                          </Badge>
                          {leave.duration && leave.duration !== "full-day" && (
                            <span className="ml-1 text-xs text-gray-500 capitalize">· {leave.duration.replace("-", " ")}</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center text-gray-700">
                            <Calendar className="h-3 w-3 mr-2 text-[#0263cc]" />
                            <span>
                              {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {Math.ceil((new Date(leave.endDate) - new Date(leave.startDate)) / (1000 * 60 * 60 * 24) + 1)} days
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`rounded-full px-3 py-1 text-sm inline-flex items-center justify-center font-medium capitalize ${getStatusStyle(
                              leave.status
                            )}`}
                          >
                            {getStatusIcon(leave.status)}
                            {leave.status}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {leave.attachments?.length > 0 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#0263cc]/20 hover:bg-[#0263cc]/10 text-[#0263cc]"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setSelectedAttachment(leave.attachments[0]);
                                setShowAttachment(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">No documents</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#0263cc]/20 hover:bg-[#0263cc]/10 text-[#0263cc]"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setEditForm({
                                  status: leave.status,
                                  remarks: leave.remarks || "",
                                });
                                setShowEditDialog(true);
                              }}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-200 hover:bg-red-50 text-red-600"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setShowDeleteDialog(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={sortedLeaves.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            currentPageCount={paginatedLeaves.length}
            itemLabel="leave requests"
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">Update Leave Request</DialogTitle>
            <DialogDescription>
              Change the status and add remarks for this leave request
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {selectedLeave && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Employee</p>
                    <p className="font-medium">{getEmployeeName(selectedLeave)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Leave Type</p>
                    <p className="font-medium capitalize">{selectedLeave.type}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">From</p>
                    <p className="font-medium">{new Date(selectedLeave.startDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">To</p>
                    <p className="font-medium">{new Date(selectedLeave.endDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <Select
                value={editForm.status}
                onValueChange={(value) =>
                  setEditForm({ ...editForm, status: value })
                }
              >
                <SelectTrigger className="w-full border-[#0263cc]/20 focus:ring-[#0263cc]/30">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <Input
                placeholder="Add remarks or comments"
                value={editForm.remarks}
                onChange={(e) =>
                  setEditForm({ ...editForm, remarks: e.target.value })
                }
                className="border-[#0263cc]/20 focus:ring-[#0263cc]/30"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setSelectedLeave(null);
                setEditForm({ status: "", remarks: "" });
              }}
              className="border-gray-200"
            >
              Cancel
            </Button>
            <Button 
              disabled={loading} 
              onClick={handleEdit}
              className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Leave Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this leave request? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {selectedLeave && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-100 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Employee</p>
                    <p className="font-medium">{getEmployeeName(selectedLeave)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Leave Type</p>
                    <p className="font-medium capitalize">{selectedLeave.type}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">From</p>
                    <p className="font-medium">{new Date(selectedLeave.startDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">To</p>
                    <p className="font-medium">{new Date(selectedLeave.endDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSelectedLeave(null);
                }}
                className="border-gray-200"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={loading}
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? "Deleting..." : "Delete Leave Request"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={showAttachment}
        onOpenChange={() => {
          setShowAttachment(false);
          setSelectedLeave(null);
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">Document Preview</DialogTitle>
            <DialogDescription>
              View and download the attached document
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {selectedAttachment && selectedLeave && (
              <div className="flex flex-col items-center">
                <div className="mb-4 w-full bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2 text-[#0263cc]">Reason for Leave:</h3>
                  <p className="text-gray-700">{selectedLeave.reason}</p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden w-full">
                  <img
                    src={selectedAttachment.url || "/placeholder.svg"}
                    alt="Document preview"
                    className="max-w-full h-auto max-h-[70vh] mx-auto"
                  />
                </div>
                <Button
                  className="mt-6 bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
                  onClick={() => window.open(selectedAttachment.url, "_blank")}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Document
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagement;
